import React from "react";
import VerticalLegend from "../LegendMenu/Legend";
import HorizontalLegend from "../LegendMenu/HorizontalLegend";
import ChartViewToggle from "./ChartViewToggle";
import { EllipsisText } from "./EllipsisText";
import "./style.css";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { ChartRenderedFromType } from "src/pages/Insights/models/interfaces";

interface IChartHeaderProps {
	title: string;
	ai_header?: string;
	subtitle?: string;
	legendWaves: Record<string, string>;
	visibleWaves?: string[];
	setVisibleWaves: (waves: string[]) => void;
	/** Explicit legend display order (e.g. from value_params). Overrides JS object-key enumeration. */
	order?: (string | number)[];
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	isChartView: boolean;
	setIsChartView: (value: boolean) => void;
	showToggle: boolean;
	layout?: "left" | "center";
	isAISummaryAvailable?: boolean;
	aiSummary?: string;
	titleMaxLines?: number;
	subtitleMaxLines?: number;

	/* Scale props */
	scaledTitleFontSize?: string | number;
	scaledLegendFontSize?: string | number;
	showSubtitle?: boolean;
	showLegend?: boolean;
	legendLines?: number;
	/** Marker shapes for line chart legends */
	waveMarkers?: Record<string, string>;
	/** Source context for conditional rendering */
	renderedFrom?: ChartRenderedFromType;
}

const ChartHeader: React.FC<IChartHeaderProps> = ({
	title,
	ai_header,
	subtitle,
	legendWaves,
	visibleWaves,
	setVisibleWaves,
	order,
	expanded,
	setExpanded,
	layout = "left",
	isAISummaryAvailable = false,
	aiSummary,
	isChartView,
	setIsChartView,
	showToggle,
	titleMaxLines = 1,
	subtitleMaxLines = 2,

	/* Scale props */
	scaledTitleFontSize,
	scaledLegendFontSize,
	showSubtitle = true,
	showLegend = true,
	legendLines,
	waveMarkers,
	renderedFrom,
}) => {
	const titleAlignment = layout === "center" ? "text-center" : "text-left";
	// When AI Summary is NOT available, show horizontal legend below title
	const showHorizontalLegend = !isAISummaryAvailable;
	const { isMultiTileSlide } = useSelector((state: RootState) => state.quantDashboardSlice);
	// Safe cast for setVisibleWaves to handle both types
	const safeSetVisibleWaves = setVisibleWaves as React.Dispatch<React.SetStateAction<string[]>>;

	return (
		<div className="px-4 pt-1 pb-1 flex w-full">
			<div className="flex-1">
				{/* Row 1: Title + Vertical Legend (when AI summary available) */}
				{renderedFrom !== ChartRenderedFromType.AskKuralInline && (
					<div className={`flex ${showHorizontalLegend ? "flex-col" : "flex-row justify-between items-start"} gap-2`}>
						{/* Vertical Legend (only when AI Summary IS available) */}
						{!showHorizontalLegend && visibleWaves && showLegend && (
							<div className="flex-shrink-0">
								<VerticalLegend waves={legendWaves} visibleWaves={visibleWaves} setVisibleWaves={setVisibleWaves} order={order} expanded={expanded} setExpanded={setExpanded} />
							</div>
						)}
					{/* Title and Subtitle */}
					<div className={`${showHorizontalLegend ? "w-full" : "flex-1 min-w-0"} ${titleAlignment} ${isMultiTileSlide ? "pr-8" : ""}`}>
							<EllipsisText
								text={renderedFrom === ChartRenderedFromType.AskKural ? ai_header : title}
								maxLines={titleMaxLines}
								lineHeight={15}
								className={`font-semibold text-[--primary-color-2] leading-tight ${renderedFrom === ChartRenderedFromType.AskKural ? "text-[--secondary-color-4]" : ""}`}
								fontSize={scaledTitleFontSize ?? 10}
							/>
						{subtitle && (!isMultiTileSlide || renderedFrom !== ChartRenderedFromType.Dashboard) && showSubtitle && (
							<div className="mt-0.5">
								<EllipsisText
									text={subtitle}
									maxLines={subtitleMaxLines}
									lineHeight={15}
									className="text-gray-500"
									fontSize={scaledLegendFontSize ?? 11}
								/>
							</div>
						)}
						</div>
					</div>
				)}

				{/* Row 2: AI Summary (future use - when available) */}
				{isAISummaryAvailable && aiSummary && <div className="mt-2 text-sm text-gray-700 leading-relaxed">{aiSummary}</div>}

			{/* Row 2: Horizontal Legend (when AI Summary is NOT available and in chartview) */}
			{showHorizontalLegend && visibleWaves && isChartView && showLegend && (
				<div className={subtitle && showSubtitle ? "mt-2" : "mt-1"}>
					<HorizontalLegend
							waves={legendWaves}
							visibleWaves={visibleWaves}
							setVisibleWaves={setVisibleWaves}
							order={order}
							expanded={expanded}
							setExpanded={setExpanded}
							displayLines={isMultiTileSlide ? 1 : 2}
							fontSize={scaledLegendFontSize}
							waveMarkers={waveMarkers}
						/>
					</div>
				)}
			</div>
			<div>{!isMultiTileSlide || renderedFrom === ChartRenderedFromType.AskKural ? <ChartViewToggle isChartView={isChartView} setIsChartView={setIsChartView} /> : null}</div>
		</div>
	);
};

export default ChartHeader;
